import axios from "@/utils/axios";
import { Topic } from "@/api/topics";

// Keeping these values defined in the frontend to avoid heavy refactoring of the existent logic
export const ACTIVITY_TYPE_ID = {
  GUESSING_GAME: 'guessing-game',
  CHAT_ABOUT_TOPIC: 'chat-about-topic',
  CHAT_ABOUT_MY_DAY: 'chat-about-my-day',
  ORDER_FROM_A_MENU: 'order-from-a-menu',
  PRACTISE_AN_INTERVIEW: 'practise-an-interview',
  PRACTICE_AN_EXAM: 'practice-an-exam',
  CHAT_ABOUT_VIDEO: 'chat-about-video',

  WARMUP: 'warmup'
}

export type ActivityTag = {
  tag: string;
  value: string;
}


export type ActivityType = {
  key: string;
  slug: string;
  name: string;
  description: string;
  duration?: number;
  timeout?: number;
  created?: string;
  end_phrase?: string | null;
  level?: string;
  order: number;
  accent?: string;
  tags: ActivityTag[];

  topics: {
    topics: Topic[];
  };

  text_data: {
    title: string;
    url: string;
    css_class: string;
    is_locked: boolean;
    goal_text: string;
    topics: {
      title: string;
      question_text: string;
      pre_text: string;
      post_text: string;
      analytics_css_mapping: string;
    };
  };
};


export default async function getActivities(): Promise<ActivityType[] | void> {
  return axios
    .then((axios) =>
      axios.get(`${process.env.NEXT_PUBLIC_API_URL}/user/activities`)
    )
    .then((response) => {
      const activities = response.data as ActivityType[];
      return activities;
    });
}
export async function getAdminActivities(): Promise<ActivityType[] | void> {
  return axios
    .then((axios) =>
      axios.get(`${process.env.NEXT_PUBLIC_API_URL}/admin/activities`)
    )
    .then((response) => response.data as ActivityType[]);
}

export async function getAdminActivityByKey(activityKey: string): Promise<ActivityType | void> {
  return axios
    .then((axios) =>
      axios.get(`${process.env.NEXT_PUBLIC_API_URL}/admin/activities/${activityKey}`)
    )
    .then((response) => response.data as ActivityType);
}

export async function createActivity(
  activity: Omit<ActivityType, 'created'>
): Promise<ActivityType> {
  return axios
    .then((axios) =>
      axios.post(`${process.env.NEXT_PUBLIC_API_URL}/admin/activities`, activity)
    )
    .then((res) => res.data as ActivityType);
}

export async function updateActivity(
  key: string,
  activity: Partial<ActivityType>
): Promise<ActivityType> {
  return axios
    .then((axios) =>
      axios.put(`${process.env.NEXT_PUBLIC_API_URL}/admin/activities/${key}`, activity)
    )
    .then((res) => res.data as ActivityType);
}

export async function duplicateActivity(
  key: string,
  slug: string,
  name: string
): Promise<ActivityType> {
  return axios
    .then((axios) =>
      axios.post(`${process.env.NEXT_PUBLIC_API_URL}/admin/activities/${key}/duplicate`, {
        slug,
        name
      })
    )
    .then((res) => res.data as ActivityType);
}

export async function deleteActivity(key: string): Promise<void> {
  return axios
    .then((axios) =>
      axios.delete(`${process.env.NEXT_PUBLIC_API_URL}/admin/activities/${key}`)
    )
    .then(() => undefined);
}

export interface LaunchDataResponse {
  activity: ActivityType | null;
  tip: {
    uuid: string | null;
    activity_id: string | null;
    text: string;
    is_active: boolean;
  } | null;
  user_data: {
    id: string;
    role: string;
    has_done_warmup: boolean;
  } | null;
  warmup_activity: ActivityType | null;
}

export async function fetchLaunchData(
  activityKey: string,
  fetchActivities?: boolean,
  fetchTip?: boolean,
  fetchUserData?: boolean,
  defaultUserRole?: string
): Promise<LaunchDataResponse> {
  return axios
    .then((axios) =>
      axios.get(`${process.env.NEXT_PUBLIC_API_URL}/user/launch-data/${activityKey}`, {
        params: {
          fetch_activities: fetchActivities,
          fetch_tip: fetchTip,
          fetch_user_data: fetchUserData,
          default_user_role: defaultUserRole
        }
      })
    )
    .then((response) => response.data as LaunchDataResponse);
}
